import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, ChevronLeft, ChevronRight, Loader2, Check, Clock, AlertTriangle, Wallet } from 'lucide-react';
import { toast } from 'sonner';

const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

export default function PaymentCalendar() {
  const [view, setView] = useState('monthly'); // daily, weekly, monthly
  const [currentDate, setCurrentDate] = useState(new Date());
  const [payments, setPayments] = useState({});
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, [currentDate, view]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const { dateFrom, dateTo } = getDateRange();
      const res = await api.get(`/treasury/payment-calendar?date_from=${dateFrom}&date_to=${dateTo}`);
      setPayments(res.data.payments || {});
      setSummary(res.data.summary || {});
    } catch (err) {
      toast.error('Ödeme takvimi yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    if (view === 'daily') {
      const dateStr = currentDate.toISOString().slice(0, 10);
      return { dateFrom: dateStr, dateTo: dateStr };
    } else if (view === 'weekly') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay() + 1); // Monday
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return { dateFrom: start.toISOString().slice(0, 10), dateTo: end.toISOString().slice(0, 10) };
    } else {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      return { dateFrom: firstDay.toISOString().slice(0, 10), dateTo: lastDay.toISOString().slice(0, 10) };
    }
  };

  const navigatePrev = () => {
    const newDate = new Date(currentDate);
    if (view === 'daily') newDate.setDate(newDate.getDate() - 1);
    else if (view === 'weekly') newDate.setDate(newDate.getDate() - 7);
    else newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const navigateNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'daily') newDate.setDate(newDate.getDate() + 1);
    else if (view === 'weekly') newDate.setDate(newDate.getDate() + 7);
    else newDate.setMonth(newDate.getMonth() + 1);
    setCurrentDate(newDate);
  };

  const handleMarkPaid = async (payment) => {
    if (payment.type === 'transaction') {
      try {
        await api.patch(`/treasury/transactions/${payment.id}/mark-paid`);
        toast.success('Ödeme işaretlendi');
        fetchPayments();
      } catch (err) {
        toast.error('Hata oluştu');
      }
    }
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'overdue': return 'bg-red-100 border-red-300 text-red-800';
      case 'today': return 'bg-yellow-100 border-yellow-300 text-yellow-800';
      case 'paid': return 'bg-green-100 border-green-300 text-green-800';
      default: return 'bg-white border-[#d0c4b0] text-[#1a1916]';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'overdue': return 'Gecikmiş';
      case 'today': return 'Bugün';
      case 'paid': return 'Ödendi';
      default: return 'Bekliyor';
    }
  };

  const getTitle = () => {
    if (view === 'daily') {
      return currentDate.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } else if (view === 'weekly') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay() + 1);
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else {
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    }
  };

  // Generate calendar grid for monthly view
  const calendarDays = useMemo(() => {
    if (view !== 'monthly') return [];
    
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Start from Monday (1) not Sunday (0)
    let startDayOfWeek = firstDay.getDay();
    if (startDayOfWeek === 0) startDayOfWeek = 7;
    startDayOfWeek -= 1; // 0 = Monday
    
    const days = [];
    
    // Empty cells before first day
    for (let i = 0; i < startDayOfWeek; i++) {
      days.push({ day: null, dateStr: null });
    }
    
    // Days of month
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      days.push({ day: d, dateStr });
    }
    
    return days;
  }, [currentDate, view]);

  // Weekly view days
  const weekDays = useMemo(() => {
    if (view !== 'weekly') return [];
    
    const start = new Date(currentDate);
    start.setDate(start.getDate() - start.getDay() + 1);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() + i);
      days.push({
        date: d,
        dateStr: d.toISOString().slice(0, 10),
        dayName: WEEKDAYS[i],
        dayNum: d.getDate()
      });
    }
    return days;
  }, [currentDate, view]);

  const PaymentCard = ({ payment }) => (
    <div className={`p-2 rounded-lg border text-xs ${getStatusStyle(payment.status)}`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold truncate">{payment.counterparty}</span>
        {!payment.is_paid && payment.type === 'transaction' && (
          <Button 
            size="sm" 
            variant="ghost" 
            className="h-5 w-5 p-0"
            onClick={(e) => { e.stopPropagation(); handleMarkPaid(payment); }}
          >
            <Check size={12} />
          </Button>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="font-mono font-semibold">
          {payment.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {payment.currency}
        </span>
        <Badge variant="outline" className={`text-[10px] px-1 ${getStatusStyle(payment.status)}`}>
          {getStatusLabel(payment.status)}
        </Badge>
      </div>
      {payment.description && (
        <p className="text-[10px] text-[#8a7e70] mt-1 truncate">{payment.description}</p>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="payment-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916]">Ödeme Takvimi</h1>
          <p className="text-sm text-[#8a7e70]">Vadesi gelen ödemeler ve çekler</p>
        </div>
        <Select value={view} onValueChange={setView}>
          <SelectTrigger className="w-[150px] bg-white border-[#d0c4b0]" data-testid="view-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Günlük</SelectItem>
            <SelectItem value="weekly">Haftalık</SelectItem>
            <SelectItem value="monthly">Aylık</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-[#faf7f2] border-[#d0c4b0]">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-[#e8dfd0] rounded-lg">
              <Wallet size={20} className="text-[#3d6e4e]" />
            </div>
            <div>
              <p className="text-xs text-[#8a7e70]">Toplam</p>
              <p className="font-bold text-[#1a1916]">{summary.total_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Check size={20} className="text-green-600" />
            </div>
            <div>
              <p className="text-xs text-green-700">Ödenen</p>
              <p className="font-bold text-green-800">{summary.paid_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock size={20} className="text-yellow-600" />
            </div>
            <div>
              <p className="text-xs text-yellow-700">Bekleyen</p>
              <p className="font-bold text-yellow-800">{summary.pending_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-xs text-red-700">Gecikmiş</p>
              <p className="font-bold text-red-800">{summary.overdue_count || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Calendar Navigation */}
      <Card className="bg-[#faf7f2] border-[#d0c4b0]">
        <CardHeader className="border-b border-[#d0c4b0] py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={navigatePrev} className="text-[#8a7e70]">
              <ChevronLeft size={20} />
            </Button>
            <CardTitle className="text-lg font-bold text-[#1a1916] flex items-center gap-2">
              <Calendar size={18} /> {getTitle()}
            </CardTitle>
            <Button variant="ghost" onClick={navigateNext} className="text-[#8a7e70]">
              <ChevronRight size={20} />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin" size={32} />
            </div>
          ) : view === 'monthly' ? (
            // Monthly Grid View
            <div className="grid grid-cols-7 gap-1">
              {/* Weekday headers */}
              {WEEKDAYS.map(day => (
                <div key={day} className="p-2 text-center text-sm font-semibold text-[#8a7e70] bg-[#e8dfd0] rounded">
                  {day}
                </div>
              ))}
              {/* Calendar cells */}
              {calendarDays.map((cell, i) => {
                const dayPayments = cell.dateStr ? (payments[cell.dateStr] || []) : [];
                const today = new Date().toISOString().slice(0, 10);
                const isToday = cell.dateStr === today;
                
                return (
                  <div 
                    key={i} 
                    className={`min-h-[100px] p-1 border rounded ${cell.day ? 'bg-white' : 'bg-gray-50'} ${isToday ? 'border-[#3d6e4e] border-2' : 'border-[#e8dfd0]'}`}
                  >
                    {cell.day && (
                      <>
                        <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-[#3d6e4e]' : 'text-[#1a1916]'}`}>
                          {cell.day}
                        </div>
                        <div className="space-y-1 max-h-[80px] overflow-y-auto">
                          {dayPayments.slice(0, 3).map((p, j) => (
                            <PaymentCard key={j} payment={p} />
                          ))}
                          {dayPayments.length > 3 && (
                            <p className="text-xs text-center text-[#8a7e70]">+{dayPayments.length - 3} daha</p>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          ) : view === 'weekly' ? (
            // Weekly View
            <div className="grid grid-cols-7 gap-2">
              {weekDays.map((day, i) => {
                const dayPayments = payments[day.dateStr] || [];
                const today = new Date().toISOString().slice(0, 10);
                const isToday = day.dateStr === today;
                
                return (
                  <div 
                    key={i}
                    className={`min-h-[200px] p-2 border rounded ${isToday ? 'border-[#3d6e4e] border-2 bg-[#3d6e4e]/5' : 'border-[#e8dfd0] bg-white'}`}
                  >
                    <div className={`text-center mb-2 pb-2 border-b ${isToday ? 'border-[#3d6e4e]' : 'border-[#e8dfd0]'}`}>
                      <p className="text-xs text-[#8a7e70]">{day.dayName}</p>
                      <p className={`text-lg font-bold ${isToday ? 'text-[#3d6e4e]' : 'text-[#1a1916]'}`}>{day.dayNum}</p>
                    </div>
                    <div className="space-y-2">
                      {dayPayments.map((p, j) => (
                        <PaymentCard key={j} payment={p} />
                      ))}
                      {dayPayments.length === 0 && (
                        <p className="text-xs text-center text-[#8a7e70] py-4">Ödeme yok</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            // Daily View
            <div className="space-y-3">
              {Object.entries(payments).map(([date, dayPayments]) => (
                <div key={date}>
                  {dayPayments.map((p, i) => (
                    <div key={i} className={`p-4 rounded-lg border mb-2 ${getStatusStyle(p.status)}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-lg">{p.counterparty}</p>
                          <p className="text-sm text-[#8a7e70]">{p.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-xl">
                            {p.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {p.currency}
                          </p>
                          <Badge className={getStatusStyle(p.status)}>{getStatusLabel(p.status)}</Badge>
                        </div>
                      </div>
                      {!p.is_paid && p.type === 'transaction' && (
                        <Button 
                          size="sm" 
                          className="mt-3 bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]"
                          onClick={() => handleMarkPaid(p)}
                        >
                          <Check size={14} className="mr-1" /> Ödendi İşaretle
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
              {Object.keys(payments).length === 0 && (
                <div className="text-center py-12 text-[#8a7e70]">
                  <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Bu tarihte ödeme bulunmuyor</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
